import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateEventCategory } from "@/features/event-categories/api/create-event-category";
import { fetchEventCategoriesKey } from "@/features/event-categories/api/fetch-event-categories";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

const createEventCategorySchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type CreateEventCategoryFormData = z.infer<typeof createEventCategorySchema>;

type CreateEventCategoryFormProps = {
  organisationId: string;
};

export function CreateEventCategoryForm({
  organisationId,
}: CreateEventCategoryFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateEventCategoryFormData>({
    resolver: zodResolver(createEventCategorySchema),
    defaultValues: {
      name: "",
    },
  });

  const {
    mutateAsync: createEventCategory,
    isPending,
    isError,
    error,
    reset,
  } = useCreateEventCategory({
    onSuccess: () => {
      form.reset();
      setIsOpen(false);
      queryClient.invalidateQueries({
        queryKey: fetchEventCategoriesKey({ organisationId }),
      });
      toast.success("Event category created successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = async (data: CreateEventCategoryFormData) => {
    await createEventCategory({
      orgId: organisationId,
      eventCategory: { name: data.name },
    });
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          form.reset();
          reset();
        }
      }}
      open={isOpen}
    >
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Event Category
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Event Category</DialogTitle>
          <DialogDescription>
            Add a new event category for this organisation.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogBody>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter event category name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {isError && (
                <div className="text-sm text-destructive">{error.message}</div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} isLoading={isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
